import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, User, ArrowUpRight, Command } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/icons';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { useOffer } from '@/context/OfferContext';
import { useAuth } from '@/context/AuthContext';
import { LogoMark } from '@/components/Logo';

export default function Header() {
  const { t, tv, lang, setLang } = useLang();
  const { services } = useServices();
  const { openOffer } = useOffer();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [location.pathname]);

  const Logo = () => (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="site-logo">
      <LogoMark size={44} ring glow />
      <span className="font-display font-semibold text-lg tracking-tight text-[color:var(--itw-text)] hidden sm:block">
        IT-WELT<span className="text-[color:var(--itw-cyan)]">360</span>
      </span>
    </Link>
  );

  return (
    <header data-testid="site-header-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled ? 'glass border-b border-[color:var(--itw-border)]' : 'bg-transparent border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            <div className="relative" onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
              <button data-testid="nav-services-trigger" className="flex items-center gap-1 px-4 py-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">
                {t('nav.services')} <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {megaOpen && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.18 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[640px]">
                    <div className="glass rounded-2xl p-3 grid grid-cols-2 gap-1 shadow-[0_18px_50px_rgba(0,0,0,0.55)]" data-testid="services-mega-menu">
                      {services.map((s) => (
                        <Link key={s.slug} to={`/leistungen/${s.slug}`} data-testid={`mega-link-${s.slug}`}
                          className="flex items-start gap-3 p-3 rounded-xl hover:bg-[color:var(--itw-surface-2)] transition-colors group">
                          <div className="h-9 w-9 shrink-0 rounded-lg bg-[color:var(--itw-surface)] border border-[color:var(--itw-border)] grid place-items-center">
                            <Icon name={s.icon} className="h-4 w-4 text-[color:var(--itw-cyan)]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[color:var(--itw-text)] flex items-center gap-1">{tv(s.name)}<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                            <div className="text-xs text-[color:var(--itw-text-3)] line-clamp-1">{tv(s.tagline)}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/ueber-uns" data-testid="nav-about" className="px-4 py-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">{t('nav.about')}</Link>
            <Link to="/status" data-testid="nav-status" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">
              Systemstatus <span className="inline-flex items-center gap-1 rounded-full bg-[#2EF2A5]/12 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-[#2EF2A5]"><span className="h-1 w-1 rounded-full bg-[#2EF2A5] animate-blink" />live</span>
            </Link>
            <Link to="/kontakt" data-testid="nav-contact" className="px-4 py-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">{t('nav.contact')}</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => window.dispatchEvent(new CustomEvent('itw:open-cmdk'))} data-testid="header-cmdk-button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[color:var(--itw-border)] text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)] transition-colors font-mono text-xs" title="Command Palette">
              <Command className="h-3.5 w-3.5" />K
            </button>
            <div className="flex items-center rounded-full border border-[color:var(--itw-border)] p-0.5" data-testid="language-toggle">
              {['de', 'en'].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${lang === l ? 'bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)]' : 'text-[color:var(--itw-text-2)]'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button onClick={() => navigate(user ? '/portal' : '/login')} data-testid="nav-portal"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">
              <User className="h-4 w-4" />{user ? t('nav.portal') : t('nav.login')}
            </button>
            <Button onClick={() => openOffer('')} data-testid="header-primary-cta-button"
              className="bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity rounded-full">
              {t('nav.requestOffer')}
            </Button>
          </div>

          <button className="lg:hidden text-[color:var(--itw-text)]" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-[color:var(--itw-border)] overflow-hidden" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-1">
              <div className="text-xs uppercase tracking-wide text-[color:var(--itw-text-3)] px-2 py-2">{t('nav.services')}</div>
              {services.map((s) => (
                <Link key={s.slug} to={`/leistungen/${s.slug}`} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[color:var(--itw-surface-2)]" data-testid={`mobile-link-${s.slug}`}>
                  <Icon name={s.icon} className="h-4 w-4 text-[color:var(--itw-cyan)]" />
                  <span className="text-sm text-[color:var(--itw-text)]">{tv(s.name)}</span>
                </Link>
              ))}
              <div className="h-px bg-[color:var(--itw-border)] my-2" />
              <Link to="/ueber-uns" className="block px-2 py-2.5 text-sm text-[color:var(--itw-text)]">{t('nav.about')}</Link>
              <Link to="/status" className="flex items-center gap-2 px-2 py-2.5 text-sm text-[color:var(--itw-text)]" data-testid="mobile-nav-status">Systemstatus <span className="text-[9px] font-mono uppercase text-[#2EF2A5]">live</span></Link>
              <Link to="/kontakt" className="block px-2 py-2.5 text-sm text-[color:var(--itw-text)]">{t('nav.contact')}</Link>
              <Link to={user ? '/portal' : '/login'} className="block px-2 py-2.5 text-sm text-[color:var(--itw-text)]">{user ? t('nav.portal') : t('nav.login')}</Link>
              <div className="flex items-center gap-3 pt-2">
                <div className="flex items-center rounded-full border border-[color:var(--itw-border)] p-0.5">
                  {['de', 'en'].map((l) => (
                    <button key={l} onClick={() => setLang(l)} className={`px-3 py-1 text-xs rounded-full ${lang === l ? 'bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)]' : 'text-[color:var(--itw-text-2)]'}`}>{l.toUpperCase()}</button>
                  ))}
                </div>
                <Button onClick={() => openOffer('')} className="flex-1 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] rounded-full">{t('nav.requestOffer')}</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
